import FullScreenLoader from '$tablet-app/src/app/components/FullScreenLoader';
import Text from '$tablet-app/src/app/components/Text';
import { useMyGoals } from '$tablet-app/src/app/goals/useGoals/useGoals';
import useCurrentDay from '$tablet-app/src/app/utils/useCurrentDay';
import { Button } from '@akin-home/buttons/native';
import { getGoalCompletionProgress } from '@akin-home/goals';
import { colors } from '@akin-home/theme';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import React, { useMemo, useState } from 'react';
import { StyleSheet } from 'react-native';
import Carousel from 'react-native-reanimated-carousel';
import styled from 'styled-components/native';
import { IndicatorDots } from '../../components/IndicatorDots';
import {
  ProgressBar,
  ProgressBarCompletedMessage,
  ProgressBarCountdown,
} from '../../goals/components/ProgressBar';
import { useIncrementGoal } from '../../goals/hooks/useIncrementGoal';
import { useMainDrawerNavigaton } from '../../navigation/hooks';
import { useCurrentUserID } from '../../users';
import DashboardPanel from '../DashboardPanel';

interface Props {
  width: number;
  small?: boolean;
}

const TodaysGoalsSingle = ({ width, small }: Props) => {
  const { navigate } = useMainDrawerNavigaton();

  const user_id = useCurrentUserID();

  /** Index for showing current goal in dots */
  const [current_index, setCurrentIndex] = useState(0);

  const { data: goals, isLoading } = useMyGoals();

  const goal = goals?.[current_index];

  const day = useCurrentDay();

  const goalKeyData = useMemo(
    () => ({
      user_id,
      date: new Date(day),
    }),
    [user_id, day]
  );

  const increment = useIncrementGoal(goalKeyData);
  const onItemPress = () => goal && increment(goal);
  const onItemLongPress = () => goal && increment(goal, -1);
  const onPressAddGoal = () => navigate('Goals');

  if (!isLoading && !goal) {
    return (
      <NoGoalsPlaceholder>
        <PlaceholderTitle>
          <FontAwesomeIcon
            icon="trophy-star"
            size={25}
            color={colors.secondary[700]}
          />
          <Text variant="h3" weight="semibold">
            Goals
          </Text>
        </PlaceholderTitle>

        <Button
          variant="secondary"
          icon="plus"
          size="large"
          onPress={onPressAddGoal}
        >
          Add your first goal
        </Button>
      </NoGoalsPlaceholder>
    );
  }

  const progress = goal ? getGoalCompletionProgress(goal, goalKeyData) : 0;
  const remaining = goal ? goal.completions_per_period - progress : 1;
  const completed = remaining === 0;

  return (
    <Panel>
      {isLoading ? (
        <FullScreenLoader />
      ) : (
        <>
          <TopSection>
            {completed ? (
              <ProgressBarCompletedMessage />
            ) : (
              <ProgressBarCountdown remaining={remaining} />
            )}

            <IndicatorDots count={goals.length} active_index={current_index} />
          </TopSection>

          <MiddleSection>
            <Carousel
              data={goals}
              width={width}
              onSnapToItem={setCurrentIndex}
              renderItem={({ item }) => (
                <DetailsSection>
                  <TitleRow style={small ? styles.small_title : undefined}>
                    {item.emoji ? (
                      <EmojiContainer
                        style={small ? styles.small_emoji_container : undefined}
                      >
                        <Text
                          style={
                            small ? styles.small_emoji : styles.large_emoji
                          }
                        >
                          {item.emoji}
                        </Text>
                      </EmojiContainer>
                    ) : null}
                    <Title
                      variant="h3"
                      weight="semibold"
                      numberOfLines={1}
                      style={small ? null : styles.large_text}
                    >
                      {item.title}
                    </Title>
                  </TitleRow>

                  <ProgressBar
                    target={item.completions_per_period}
                    current={
                      item ? getGoalCompletionProgress(item, goalKeyData) : 0
                    }
                  />
                </DetailsSection>
              )}
            />
          </MiddleSection>

          <ButtonsSection>
            {progress > 0 ? (
              <ActionButton
                variant="text"
                icon="arrow-rotate-left"
                onPress={onItemLongPress}
              >
                Undo
              </ActionButton>
            ) : null}

            {!completed ? (
              <ActionButton
                variant="secondary"
                icon="check"
                onPress={onItemPress}
              >
                Done
              </ActionButton>
            ) : null}
          </ButtonsSection>
        </>
      )}
    </Panel>
  );
};

const styles = StyleSheet.create({
  small_title: {
    flexDirection: 'column',
  },
  small_emoji_container: {
    height: 88,
    width: 88,
  },
  small_emoji: {
    fontSize: 18,
  },
  large_emoji: {
    fontSize: 30,
  },
  carousel: {
    flex: 1,
  },
  large_text: {
    flex: 1,
  },
});

const Panel = styled(DashboardPanel)`
  flex: 1;
  gap: 10px;
`;

const TopSection = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 16px 16px 0;
`;

const MiddleSection = styled.View`
  width: 100%;
  flex: 1;
  align-items: center;
`;

const DetailsSection = styled.View`
  gap: 24px;
  padding-horizontal: 16px;
  margin-top: auto;
  margin-bottom: auto;
`;

const TitleRow = styled.View`
  flex-direction: row;
  align-items: center;
  gap: 15px;
`;

const Title = styled(Text)`
  overflow: hidden;
  flex-wrap: wrap;
`;

const NoGoalsPlaceholder = styled(DashboardPanel)`
  flex: 1;
  align-items: center;
  justify-content: center;
  background-color: ${colors.blue.background};
  gap: 10px;
`;

const PlaceholderTitle = styled.View`
  flex-direction: row;
  gap: 14px;
  align-items: center;
  justify-content: center;
`;

const ButtonsSection = styled.View`
  flex-direction: row;
  align-items: center;
  padding: 0 16px 16px;
`;

const ActionButton = styled(Button)`
  flex: 1;
`;

const EmojiContainer = styled.View`
  align-items: center;
  justify-content: center;
  height: 100px;
  width: 100px;
  border-radius: 62px;
  background-color: rgba(83, 90, 241, 0.1);
`;

export default TodaysGoalsSingle;
